<template>
    <el-dialog v-model="visible" title="商户开通数电发票" width="650px" destroy-on-close @closed="resetForm">
        <el-form ref="formRef" :model="formData" :rules="rules" label-width="140px" style="padding-right: 20px;">
            <el-form-item label="开通通道:" prop="channel">
                <el-radio-group v-model="formData.channel" @change="handleChannelChange">
                    <el-radio :label="1">微信数电发票</el-radio>
                    <el-radio :label="2">支付宝数电发票</el-radio>
                </el-radio-group>
            </el-form-item>

            <el-form-item label="服务商编号:" prop="agentId">
                <el-input v-model="formData.agentId" placeholder="请输入服务商编号" clearable />
            </el-form-item>

            <el-form-item label="乐刷商户号:" prop="merchantId">
                <el-input v-model="formData.merchantId" placeholder="请输入乐刷商户号" clearable />
            </el-form-item>

            <el-form-item label="通道子商户号:" prop="sub_mchid">
                <el-input v-model="formData.sub_mchid" placeholder="微信必传，支付宝选填" clearable />
            </el-form-item>

            <el-form-item label="企业税号:" prop="tax_no">
                <el-input v-model="formData.tax_no" placeholder="请与营业执照上的统一社会信用代码保持一致" clearable />
            </el-form-item>

            <el-form-item label="开通产品类型:" prop="product_id">
                <el-select v-model="formData.product_id" placeholder="请选择产品类型" style="width: 100%;">
                    <el-option v-for="item in productOptions" :key="item.value" :label="item.label"
                        :value="item.value" />
                </el-select>
            </el-form-item>
        </el-form>

        <template #footer>
            <div class="dialog-footer">
                <el-button @click="visible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="loading">提交申请</el-button>
            </div>
        </template>
    </el-dialog>

    <el-dialog v-model="resultVisible" title="开通申请结果" width="500px" center append-to-body>
        <div v-if="resultData" class="result-container">
            <el-result icon="success" title="申请提交成功" sub-title="请引导商户完成授权操作">
                <template #extra>
                    <div v-if="formData.channel === 1" class="wechat-result">
                        <p class="tips">请商户使用微信扫描下方二维码跳转至小程序开通：</p>
                        <div class="qrcode-placeholder">
                            <span>{{ resultData.inviteUrl }}</span>
                            <p style="font-size: 12px; color:#999; margin-top:5px">(此处应使用 inviteUrl 生成二维码)</p>
                        </div>
                        <div class="extra-info" v-if="resultData.miniprogramAppid">
                            小程序AppID: {{ resultData.miniprogramAppid }}
                        </div>
                    </div>

                    <div v-if="formData.channel === 2" class="alipay-result">
                        <p class="tips">请使用PC浏览器打开下方链接，使用企业支付宝扫码授权：</p>
                        <el-input v-model="resultData.inviteUrl" readonly>
                            <template #append>
                                <el-button @click="copyUrl(resultData.inviteUrl)">复制</el-button>
                            </template>
                        </el-input>
                        <div class="extra-info" v-if="resultData.businessId">
                            业务申请ID: {{ resultData.businessId }}
                        </div>
                    </div>
                </template>
            </el-result>
        </div>
        <template #footer>
            <div class="dialog-footer">
                <el-button type="primary" @click="closeAllResult">关闭</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, defineExpose } from 'vue'
import { ElMessage } from 'element-plus'

const visible = ref(false)
const formRef = ref(null)
const loading = ref(false)
const resultVisible = ref(false)
const resultData = ref(null)

// 暴露给父组件的方法
const open = (row) => {
    visible.value = true
    // 自动带入商户列表中的商户号 (根据您列表的字段，这里猜测使用 no)
    if (row && row.no) {
        formData.merchantId = row.no
    }
}
defineExpose({ open })

const formData = reactive({
    channel: 1,
    agentId: '',
    merchantId: '',
    sub_mchid: '',
    tax_no: '',
    product_id: '',
    businessid: ''
})

const productOptions = computed(() => {
    if (formData.channel === 1) {
        return [
            { label: '基础开票能力 (适合餐饮、酒店等)', value: 'BASE_ABILITY' },
            { label: '成品油能力 (适合石油石化等行业)', value: 'REFINED_OIL_ABILITY' },
            { label: '不动产租赁能力 (适合停车场等)', value: 'REAL_ESTATE_ABILITY' }
        ]
    } else {
        return [
            { label: '成品油发票', value: '2025060044528616' }
        ]
    }
})

const validateSubMchid = (rule, value, callback) => {
    if (formData.channel === 1 && !value) {
        callback(new Error('微信通道下子商户号必传'))
    } else {
        callback()
    }
}

const rules = reactive({
    channel: [{ required: true, message: '请选择开通通道', trigger: 'change' }],
    agentId: [{ required: true, message: '请输入服务商编号', trigger: 'blur' }],
    merchantId: [{ required: true, message: '请输入乐刷商户号', trigger: 'blur' }],
    sub_mchid: [{ validator: validateSubMchid, trigger: 'blur' }],
    tax_no: [{ required: true, message: '请输入企业税号', trigger: 'blur' }],
    product_id: [{ required: true, message: '请选择开通产品类型', trigger: 'change' }]
})

const handleChannelChange = () => {
    formData.product_id = ''
    if (formRef.value) formRef.value.clearValidate('sub_mchid')
}

const generateBusinessId = (agentId) => {
    const now = new Date()
    const pad = (n) => (n < 10 ? '0' + n : n)
    const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    const random6 = Math.floor(100000 + Math.random() * 900000)
    return `${agentId}${dateStr}${random6}`
}

const copyUrl = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        ElMessage.success('链接已复制到剪贴板')
    }).catch(() => {
        ElMessage.error('复制失败，请手动复制')
    })
}

const resetForm = () => {
    if (formRef.value) formRef.value.resetFields()
    formData.channel = 1
}

const closeAllResult = () => {
    resultVisible.value = false
    visible.value = false
}

const submitForm = async () => {
    if (!formRef.value) return

    await formRef.value.validate((valid) => {
        if (valid) {
            loading.value = true
            const payload = { ...formData }

            if (payload.channel === 2) {
                payload.businessid = generateBusinessId(payload.agentId)
            } else {
                delete payload.businessid
            }

            // 模拟 API 请求响应
            setTimeout(() => {
                loading.value = false
                resultData.value = {
                    inviteUrl: payload.channel === 1 ? 'weixin://qr/mock-code-123' : 'https://b.alipay.com/page/mock-invoice-open',
                    businessId: payload.channel === 2 ? payload.businessid : undefined,
                    miniprogramAppid: payload.channel === 1 ? 'wx1234567890abcdef' : undefined
                }
                resultVisible.value = true
            }, 800)
        } else {
            ElMessage.error('请完善必填信息')
        }
    })
}
</script>

<style lang="less" scoped>
.result-container {
    .tips {
        font-size: 14px;
        color: #606266;
        margin-bottom: 15px;
        text-align: center;
    }

    .qrcode-placeholder {
        width: 180px;
        height: 180px;
        background-color: #f2f2f2;
        border: 1px dashed #d9d9d9;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        word-break: break-all;
        padding: 10px;
        text-align: center;
    }

    .extra-info {
        margin-top: 15px;
        font-size: 13px;
        color: #909399;
        text-align: center;
    }
}
</style>